import { z } from 'zod';

/**
 * Reported state of a single mobile OS permission. Mobile clients
 * (carrier-app, customer-app) call `PATCH /current-user/meta` with
 * whatever the OS is currently reporting.
 *
 * - `granted` — the permission is fully granted.
 * - `denied` — the user actively denied it.
 * - `blocked` — permanently denied at the OS level (must go to
 *   Settings to re-enable).
 * - `undetermined` — the prompt has never been shown.
 * - `restricted` — iOS-only; parental controls / MDM restrict it.
 * - `unknown` — client couldn't determine the state.
 */
export const userMetaPermissionStateSchema = z.enum([
  'granted',
  'denied',
  'blocked',
  'undetermined',
  'restricted',
  'unknown',
]);
export type UserMetaPermissionState = z.infer<typeof userMetaPermissionStateSchema>;

/**
 * Snapshot of a permission's state at a point in time.
 *
 * `capturedAt` is required so consumers can tell "this user
 * granted location 3 minutes ago" apart from "this user granted
 * location a year ago (probably still granted, but stale)".
 */
export const userMetaPermissionSnapshotSchema = z.object({
  state: userMetaPermissionStateSchema,
  capturedAt: z.date(),
});
export type UserMetaPermissionSnapshot = z.infer<typeof userMetaPermissionSnapshotSchema>;

/**
 * Full permissions block on the user. See `docs/ai/user-signals.md`.
 *
 * We track the three permissions that actually gate features
 * agents care about: fine location (drives `meta.location.accurate`),
 * background location (unlocks passive fleet tracking), and
 * notifications (drives push reachability).
 */
export const userMetaPermissionsSchema = z.object({
  fineLocation: userMetaPermissionSnapshotSchema.optional(),
  backgroundLocation: userMetaPermissionSnapshotSchema.optional(),
  notifications: userMetaPermissionSnapshotSchema.optional(),
});
export type UserMetaPermissions = z.infer<typeof userMetaPermissionsSchema>;
